import { useState, useMemo, useRef } from 'react';
import { getWeekDate } from '../data/promo-calendar';
import { usePromoGrid, usePromoMutations, FY } from '../hooks/usePromoGrid';
import Button from '../components/ui/Button';
import { Input, Select, Label } from '../components/ui/Input';

const WEEKS = Array.from({ length: 52 }, (_, i) => i + 1);
const CURRENT_WEEK = 37; // last completed scan week (FY2026)
const STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];

const PROMO_ROWS = ['PROMO PRICE', 'PROMO/MEDIA', 'SCAN DEAL', 'ACTIVATION', 'STORE TIE UP'];

// ── Cell styling ────────────────────────────────────────────────────────────

// Deal-grid cell styling — each measure (and sub-deal-type for Promo Price /
// Promo Media) gets a paired soft-bg + accent-color treatment. Token-driven
// so dark mode + future re-skinning work for free.
function getCellStyle(measure, val) {
  if (!val) return { bg: 'transparent', color: 'var(--text-muted)' };

  if (measure === 'STORE TIE UP') {
    return { bg: 'var(--bg-input)', color: 'var(--text-body)' };
  }
  if (measure === 'ACTIVATION') {
    return { bg: 'var(--warn-soft)', color: 'var(--warn-text)' };
  }
  if (measure === 'SCAN DEAL') {
    return { bg: 'var(--info-soft)', color: 'var(--info)' };
  }
  if (measure === 'PROMO/MEDIA') {
    if (val.includes('CAT') || val.includes('DIGITAL') || val.includes('BEACON') || val.includes('DISPLAY'))
      return { bg: 'var(--ai-soft)', color: 'var(--ai-text)' };
    return { bg: 'var(--ai-soft)', color: 'var(--text-body)' };
  }
  // PROMO PRICE — split by deal-type so the colour codifies the discount style.
  if (val.includes('50%')) return { bg: 'var(--success-soft)', color: 'var(--success)' };
  if (val.includes('FOR $') || val.includes('FOR$')) return { bg: 'var(--info-soft)', color: 'var(--info)' };
  if (val.includes('LOW PRICE')) return { bg: 'var(--danger-soft)', color: 'var(--danger)' };
  return { bg: 'var(--border)', color: 'var(--text-primary)' };
}

function getHeaderStyle(type) {
  if (type === 'majans') return { bg: 'var(--warn-soft)', color: 'var(--warn-text)', label: 'var(--warn)' };
  if (type === 'ww')     return { bg: 'var(--success-soft)', color: 'var(--success)', label: 'var(--success-border)' };
  if (type === 'coles')  return { bg: 'var(--info-soft)', color: 'var(--info)', label: 'var(--info-border)' };
  if (type === 'school') return { bg: 'var(--bg-input)', color: 'var(--text-muted)', label: 'var(--border)' };
  if (type === 'marketing') return { bg: 'var(--ai-soft)', color: 'var(--ai-text)', label: 'var(--ai-border)' };
  if (type === 'sales-extra') return { bg: 'var(--border)', color: 'var(--text-body)', label: 'var(--border-soft)' };
  return { bg: 'transparent', color: 'var(--text-muted)', label: 'var(--border)' };
}

// ── Sticky label cell ───────────────────────────────────────────────────────

function LabelCell({ children, style = {} }) {
  return (
    <td style={{
      position: 'sticky', left: 0, zIndex: 2,
      background: 'var(--bg-elevated)', padding: '3px 10px',
      fontSize: 10, fontWeight: 500, whiteSpace: 'nowrap',
      borderRight: '1px solid var(--border)',
      ...style,
    }}>
      {children}
    </td>
  );
}

// ── Week header ─────────────────────────────────────────────────────────────

function WeekHeaders({ keyDates, colWidth = 54 }) {
  return (
    <tr style={{ background: 'var(--bg-elevated)', position: 'sticky', top: 0, zIndex: 3 }}>
      <th style={{
        position: 'sticky', left: 0, zIndex: 4, background: 'var(--bg-elevated)',
        width: 130, minWidth: 130, padding: '4px 10px',
        fontSize: 10, color: 'var(--text-muted)', fontWeight: 600,
        borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
      }}>
        Row
      </th>
      {WEEKS.map(w => (
        <th key={w} style={{
          minWidth: colWidth, width: colWidth, textAlign: 'center',
          padding: '3px 1px', borderBottom: '1px solid var(--border)',
          borderTop: w === CURRENT_WEEK ? '2px solid #6366f1' : undefined,
          background: w === CURRENT_WEEK ? 'color-mix(in srgb, var(--bg-elevated) 60%, transparent)' : undefined,
          color: keyDates[String(w)] ? 'var(--warn)' : w === CURRENT_WEEK ? '#818cf8' : 'var(--text-muted)',
        }}>
          <div style={{ fontSize: 10, fontWeight: 600 }}>W{w}</div>
          <div style={{ fontSize: 8, fontWeight: 400, color: 'var(--text-muted)' }}>{getWeekDate(w)}</div>
        </th>
      ))}
    </tr>
  );
}

// ── Event row (key dates / school terms) ────────────────────────────────────

function EventRow({ label, data, type, colWidth = 54 }) {
  const st = getHeaderStyle(type);
  if (Object.keys(data).length === 0) return null;

  return (
    <tr>
      <LabelCell style={{ background: st.label, color: st.color, borderBottom: '1px solid var(--bg-elevated)' }}>
        {label}
      </LabelCell>
      {WEEKS.map(w => {
        const val = data[String(w)];
        return (
          <td key={w} style={{
            textAlign: 'center', padding: '2px 1px',
            background: val ? st.bg : w === CURRENT_WEEK ? 'color-mix(in srgb, var(--bg-elevated) 27%, transparent)' : 'transparent',
            borderBottom: '1px solid var(--bg-elevated)',
            borderLeft: w === CURRENT_WEEK ? '1px solid #6366f166' : undefined,
            minWidth: colWidth,
          }}>
            {val && (
              <span style={{
                display: 'block', fontSize: 8, color: st.color,
                padding: '1px 2px', lineHeight: 1.3,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                maxWidth: colWidth - 4,
              }} title={val}>
                {val}
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
}

// ── Span event row (colSpan bands for multi-week events) ────────────────────

function SpanEventRow({ label, spans, type, colWidth = 54 }) {
  const st = getHeaderStyle(type);
  if (!spans || spans.length === 0) return null;

  const sorted = [...spans].sort((a, b) => a.startWeek - b.startWeek);

  const cells = [];
  let w = 1;
  for (const span of sorted) {
    // Clamp to the visible window and to the running cursor so overlapping or
    // out-of-range spans can never push the row past 52 columns.
    const start = Math.max(span.startWeek, w);
    const end = Math.min(span.endWeek, 52);
    if (end < start) continue;
    while (w < start) {
      cells.push(<td key={`gap-${w}`} style={{ minWidth: colWidth, borderBottom: '1px solid var(--bg-elevated)' }} />);
      w++;
    }
    const spanLen = end - start + 1;
    cells.push(
      <td key={`span-${w}`} colSpan={spanLen} style={{
        background: st.bg,
        borderBottom: '1px solid var(--bg-elevated)',
        borderLeft: `2px solid ${st.color}40`,
        borderRight: `1px solid var(--bg-elevated)`,
        padding: '2px 5px', overflow: 'hidden', verticalAlign: 'middle',
      }}>
        <span style={{
          display: 'block', fontSize: 8, color: st.color, fontWeight: 600,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.4,
        }} title={span.dates ? `${span.label} (${span.dates})` : span.label}>
          {span.label}
          {span.dates && <span style={{ fontWeight: 400, opacity: 0.65, marginLeft: 4 }}>{span.dates}</span>}
        </span>
      </td>
    );
    w = end + 1;
  }
  while (w <= 52) {
    cells.push(<td key={`tail-${w}`} style={{ minWidth: colWidth, borderBottom: '1px solid var(--bg-elevated)' }} />);
    w++;
  }

  return (
    <tr>
      <LabelCell style={{ background: st.label, color: st.color, borderBottom: '1px solid var(--bg-elevated)' }}>
        {label}
      </LabelCell>
      {cells}
    </tr>
  );
}

// ── Editable cell (edit mode only) ──────────────────────────────────────────

function EditableCell({ value, onCommit, onClose }) {
  const [draft, setDraft] = useState(value ?? '');
  const cancelled = useRef(false);
  return (
    <input
      autoFocus
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => {
        if (!cancelled.current) {
          const v = draft.trim();
          if (v !== (value ?? '')) onCommit(v);
        }
        onClose();
      }}
      onKeyDown={e => {
        if (e.key === 'Enter') e.currentTarget.blur();
        else if (e.key === 'Escape') { cancelled.current = true; e.currentTarget.blur(); }
      }}
      style={{
        width: '100%', boxSizing: 'border-box', fontSize: 9, padding: '1px 2px',
        textAlign: 'center', border: '1px solid var(--info)', borderRadius: 3,
        background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none',
      }}
    />
  );
}

// ── Assortment section ──────────────────────────────────────────────────────

function AssortmentSection({ promo, retailer, colWidth = 54, editMode, editingCell, setEditingCell, onCommitCell, onRemove }) {
  // In edit mode show all measure rows (so blanks can be filled); otherwise
  // only rows that already carry data.
  const rows = editMode
    ? PROMO_ROWS.filter(r => r !== 'STORE TIE UP')
    : PROMO_ROWS.filter(r => Object.keys(promo.data[r] || {}).length > 0);

  if (rows.length === 0 && !editMode) return null;

  return (
    <>
      {/* Assortment sub-header */}
      <tr>
        <td colSpan={53} style={{
          background: 'var(--bg-elevated)', padding: '5px 10px',
          fontSize: 11, fontWeight: 600, color: 'var(--text-primary)',
          borderTop: '2px solid var(--border)', borderBottom: '1px solid var(--border)',
          position: 'sticky', left: 0,
        }}>
          <span style={{
            display: 'inline-block', fontSize: 9, fontWeight: 600,
            color: retailer === 'Coles' ? 'var(--info)' : 'var(--success)',
            background: retailer === 'Coles' ? 'var(--info-soft)' : 'var(--success-soft)',
            borderRadius: 3, padding: '1px 5px', marginRight: 6,
          }}>
            {retailer}
          </span>
          {promo.assortment}
          <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6, fontSize: 10 }}>
            {promo.brand}
          </span>
          {editMode && (
            <button
              onClick={() => onRemove(promo.assortmentId, promo.assortment)}
              title="Remove this SKU"
              style={{
                float: 'right', background: 'var(--danger-soft)', color: 'var(--danger)',
                border: '1px solid var(--danger-border)', borderRadius: 4, cursor: 'pointer',
                fontSize: 10, fontWeight: 600, padding: '1px 7px', lineHeight: 1.4,
              }}>
              Remove
            </button>
          )}
        </td>
      </tr>

      {rows.map(measure => (
        <tr key={measure}>
          <LabelCell style={{
            color: measure === 'ACTIVATION' ? 'var(--warn)'
              : measure === 'STORE TIE UP' ? 'var(--text-muted)'
              : measure === 'SCAN DEAL' ? 'var(--info)'
              : measure === 'PROMO/MEDIA' ? 'var(--ai)'
              : 'var(--text-body)',
            borderBottom: '1px solid var(--border)', fontSize: 10,
          }}>
            {measure}
          </LabelCell>
          {WEEKS.map(w => {
            const val = promo.data[measure]?.[String(w)];
            const { bg, color } = getCellStyle(measure, val);
            const isEditing = editMode && editingCell
              && editingCell.assortmentId === promo.assortmentId
              && editingCell.measure === measure && editingCell.week === w;
            return (
              <td key={w}
                onClick={editMode && !isEditing ? () => setEditingCell({ assortmentId: promo.assortmentId, measure, week: w }) : undefined}
                style={{
                  textAlign: 'center', padding: isEditing ? 0 : '2px 1px',
                  cursor: editMode && !isEditing ? 'pointer' : 'default',
                  background: isEditing ? 'var(--bg-elevated)' : (bg || (w === CURRENT_WEEK ? 'color-mix(in srgb, var(--bg-elevated) 27%, transparent)' : undefined)),
                  borderBottom: '1px solid var(--border)',
                  borderLeft: w === CURRENT_WEEK ? '1px solid #6366f166' : undefined,
                  minWidth: colWidth,
                  boxShadow: editMode && !isEditing ? 'inset 0 0 0 1px var(--border-soft)' : undefined,
                }}>
                {isEditing ? (
                  <EditableCell
                    value={val}
                    onCommit={(v) => onCommitCell(promo.assortmentId, measure, w, v)}
                    onClose={() => setEditingCell(null)}
                  />
                ) : (
                  val && (
                    <span style={{
                      display: 'block', fontSize: 9, color, fontWeight: 500,
                      padding: '1px 2px', lineHeight: 1.3,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      maxWidth: colWidth - 4,
                    }} title={val}>
                      {val}
                    </span>
                  )
                )}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

// ── Add-SKU inline panel ────────────────────────────────────────────────────

function AddSkuPanel({ grid, onAdd, onClose, busy }) {
  const [ret, setRet] = useState('Woolworths');
  const [brand, setBrand] = useState('');
  const [name, setName] = useState('');
  const submit = () => {
    if (!name.trim()) return;
    onAdd({
      accountId: ret === 'Woolworths' ? grid.wwAccountId : grid.colesAccountId,
      assortment: name.trim(),
      brand: brand.trim(),
    });
  };
  return (
    <div className="card" style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div>
        <Label>Retailer</Label>
        <Select value={ret} onChange={e => setRet(e.target.value)} style={{ width: 150 }}>
          <option>Woolworths</option>
          <option>Coles</option>
        </Select>
      </div>
      <div>
        <Label>Brand</Label>
        <Input value={brand} onChange={e => setBrand(e.target.value)} style={{ width: 170 }} placeholder="Bhuja" />
      </div>
      <div style={{ flex: 1, minWidth: 220 }}>
        <Label>Assortment</Label>
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Bhuja 200g"
          onKeyDown={e => { if (e.key === 'Enter') submit(); }} />
      </div>
      <Button variant="primary" disabled={busy || !name.trim()} onClick={submit}>{busy ? 'Adding…' : 'Add SKU'}</Button>
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
    </div>
  );
}

// ── Events management (sales + marketing) ───────────────────────────────────

// Suggested lane keys (free text also allowed). Sales lanes match the bespoke
// calendar rows; marketing lanes render as generic colour-coded bands.
const EVENT_LANES = ['majans', 'ww', 'coles', 'ww-campaign', 'ww-seasonal', 'coles-campaign',
  'retail-media', 'meta', 'atl', 'free-media', 'sampling', 'sponsorship'];

function EventForm({ initial, onSave, onCancel, busy }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const sw = Number(f.startWeek);
  const valid = f.scope.trim() && f.label.trim() && sw >= 1 && sw <= 52
    && (f.kind === 'point' || (Number(f.endWeek) >= sw && Number(f.endWeek) <= 52));
  const submit = () => {
    if (!valid) return;
    onSave({
      eventId: f.eventId || undefined,
      category: f.category,
      scope: f.scope.trim(),
      kind: f.kind,
      startWeek: sw,
      endWeek: f.kind === 'span' ? Number(f.endWeek || f.startWeek) : null,
      label: f.label.trim(),
      dates: f.dates.trim() || null,
    });
  };
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', padding: '10px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
      <div><Label>Category</Label><Select value={f.category} onChange={set('category')} style={{ width: 110 }}><option value="sales">Sales</option><option value="marketing">Marketing</option></Select></div>
      <div><Label>Lane</Label><Input list="promo-lanes" value={f.scope} onChange={set('scope')} style={{ width: 130 }} placeholder="e.g. meta" /></div>
      <div><Label>Type</Label><Select value={f.kind} onChange={set('kind')} style={{ width: 90 }}><option value="point">Point</option><option value="span">Span</option></Select></div>
      <div><Label>Week</Label><Input type="number" min="1" max="52" value={f.startWeek} onChange={set('startWeek')} style={{ width: 64 }} /></div>
      {f.kind === 'span' && <div><Label>End wk</Label><Input type="number" min="1" max="52" value={f.endWeek} onChange={set('endWeek')} style={{ width: 64 }} /></div>}
      <div style={{ flex: 1, minWidth: 150 }}><Label>Label</Label><Input value={f.label} onChange={set('label')} placeholder="e.g. Meta Always-On" onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} /></div>
      <div><Label>Dates (optional)</Label><Input value={f.dates} onChange={set('dates')} style={{ width: 130 }} placeholder="wc 24/9–10/11" /></div>
      <Button variant="primary" disabled={busy || !valid} onClick={submit}>{busy ? 'Saving…' : (f.eventId ? 'Update' : 'Add')}</Button>
      <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      <datalist id="promo-lanes">{EVENT_LANES.map((s) => <option key={s} value={s} />)}</datalist>
    </div>
  );
}

function EventList({ title, accent, items, onEdit, onDelete }) {
  return (
    <div style={{ flex: 1, minWidth: 300 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
        {title} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· {items.length}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {items.length === 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)', padding: '4px 0' }}>None yet.</div>}
        {items.map((e) => (
          <div key={e.eventId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, padding: '3px 6px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 4 }}>
            <span style={{ color: accent, fontWeight: 600, minWidth: 92, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.scope}</span>
            <span style={{ color: 'var(--text-muted)', minWidth: 60 }}>W{e.startWeek}{e.kind === 'span' && e.endWeek ? `–${e.endWeek}` : ''}</span>
            <span style={{ flex: 1, color: 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.label}{e.dates ? ` (${e.dates})` : ''}</span>
            <button onClick={() => onEdit(e)} title="Edit" style={{ background: 'none', border: '1px solid var(--border-soft)', borderRadius: 4, color: 'var(--text-muted)', cursor: 'pointer', fontSize: 10, padding: '1px 7px' }}>Edit</button>
            <button onClick={() => onDelete(e)} title="Delete" style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 4, color: 'var(--danger)', cursor: 'pointer', fontSize: 11, padding: '1px 7px', lineHeight: 1 }}>×</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventsPanel({ events, onSave, onDelete, busy }) {
  const [editing, setEditing] = useState(null);
  const blank = (category) => ({ eventId: 0, category, scope: '', kind: 'point', startWeek: 1, endWeek: '', label: '', dates: '' });
  const toForm = (e) => ({ eventId: e.eventId, category: e.category || 'sales', scope: e.scope, kind: e.kind, startWeek: e.startWeek, endWeek: e.endWeek ?? '', label: e.label, dates: e.dates ?? '' });
  const sales = events.filter((e) => (e.category || 'sales') !== 'marketing');
  const mkt = events.filter((e) => e.category === 'marketing');
  const del = (e) => { if (window.confirm(`Delete event "${e.label}"?`)) onDelete(e.eventId); };
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>Sales &amp; Marketing Events</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Point = single week · Span = week range. These render on the calendar above.</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <Button variant="default" size="sm" onClick={() => setEditing(blank('sales'))}>+ Sales event</Button>
          <Button variant="ai-soft" size="sm" onClick={() => setEditing(blank('marketing'))}>+ Marketing event</Button>
        </div>
      </div>
      {editing && <EventForm initial={editing} busy={busy} onCancel={() => setEditing(null)} onSave={(p) => onSave(p, { onSuccess: () => setEditing(null) })} />}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        <EventList title="Sales events" accent="var(--info)" items={sales} onEdit={(e) => setEditing(toForm(e))} onDelete={del} />
        <EventList title="Marketing events" accent="var(--ai)" items={mkt} onEdit={(e) => setEditing(toForm(e))} onDelete={del} />
      </div>
    </div>
  );
}

// ── Excel export (client-side, lazy-loaded) ─────────────────────────────────

async function exportToExcel(grid) {
  const XLSX = await import('xlsx');
  const header = ['Retailer', 'Assortment', 'Brand', 'Ref', 'Measure', ...WEEKS.map(w => `W${w}`)];
  const rows = [header];
  const addBlock = (retailer, promos) => {
    for (const p of promos) {
      for (const measure of PROMO_ROWS) {
        const m = p.data[measure];
        if (!m || Object.keys(m).length === 0) continue;
        rows.push([retailer, p.assortment, p.brand || '', p.ref || '', measure, ...WEEKS.map(w => m[String(w)] ?? '')]);
      }
    }
  };
  addBlock('Woolworths', grid.wwPromos);
  addBlock('Coles', grid.colesPromos);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), `Promotions FY${FY}`);

  const evHeader = ['Category', 'Scope', 'Kind', 'StartWeek', 'EndWeek', 'Label', 'Dates'];
  const evRows = [evHeader, ...(grid.raw.events || []).map(e => [e.category || 'sales', e.scope, e.kind, e.startWeek, e.endWeek ?? '', e.label, e.dates ?? ''])];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(evRows), 'Events');

  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `Promotions_FY${FY}_${stamp}.xlsx`);
}

// ── Main component ──────────────────────────────────────────────────────────

export default function Promotions() {
  const { grid, isLoading, error, isFetching } = usePromoGrid();
  const { saveCell, addAssortment, removeAssortment, upsertEvent, deleteEvent } = usePromoMutations();

  const [retailer, setRetailer] = useState('Both');
  const [selectedAssts, setSelectedAssts] = useState(new Set());
  const [editMode, setEditMode] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const [exporting, setExporting] = useState(false);

  const allAssts = useMemo(() => {
    if (!grid) return [];
    const list = [];
    if (retailer === 'Woolworths' || retailer === 'Both') grid.wwPromos.forEach(p => list.push({ ...p, retailer: 'Woolworths' }));
    if (retailer === 'Coles' || retailer === 'Both') grid.colesPromos.forEach(p => list.push({ ...p, retailer: 'Coles' }));
    return list;
  }, [grid, retailer]);

  const asstNames = useMemo(() => [...new Set(allAssts.map(p => p.assortment))], [allAssts]);

  const visiblePromos = useMemo(() => {
    if (selectedAssts.size === 0) return allAssts;
    return allAssts.filter(p => selectedAssts.has(p.assortment));
  }, [allAssts, selectedAssts]);

  function toggleAsst(name) {
    setSelectedAssts(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  }
  function changeRetailer(r) { setRetailer(r); setSelectedAssts(new Set()); }
  function commitCell(assortmentId, measure, week, value) { saveCell.mutate({ assortmentId, measure, week, value }); }
  function handleRemove(assortmentId, name) {
    if (window.confirm(`Remove "${name}" and all its promo cells from the grid?`)) removeAssortment.mutate(assortmentId);
  }
  function handleAdd(payload) { addAssortment.mutate(payload, { onSuccess: () => setShowAdd(false) }); }
  function onSaveEvent(payload, opts) { upsertEvent.mutate(payload, opts); }
  function onDeleteEvent(eventId) { deleteEvent.mutate(eventId); }
  async function handleExport() {
    setExporting(true);
    try { await exportToExcel(grid); }
    catch (e) { window.alert(`Export failed: ${e.message}`); }
    finally { setExporting(false); }
  }

  if (isLoading) {
    return <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-muted)' }}>Loading promotions…</div>;
  }
  if (error) {
    return (
      <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--danger)' }}>
        Failed to load the promotions grid: {error.message}
      </div>
    );
  }

  const showWW    = retailer === 'Woolworths' || retailer === 'Both';
  const showColes = retailer === 'Coles'      || retailer === 'Both';
  const saving = saveCell.isPending || addAssortment.isPending || removeAssortment.isPending || upsertEvent.isPending || deleteEvent.isPending;
  const saveErr = saveCell.error || addAssortment.error || removeAssortment.error || upsertEvent.error || deleteEvent.error;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Toolbar ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <span style={{ color: 'var(--text-muted)', fontSize: 12, flexShrink: 0 }}>Retailer</span>
        {['Both', 'Woolworths', 'Coles'].map(r => (
          <button key={r} className={retailer === r ? 'pill-active' : 'pill-inactive'} onClick={() => changeRetailer(r)}>{r}</button>
        ))}

        {asstNames.length > 0 && (
          <>
            <div style={{ width: 1, height: 20, background: 'var(--border-soft)', margin: '0 4px' }} />
            <span style={{ color: 'var(--text-muted)', fontSize: 12, flexShrink: 0 }}>Assortment</span>
            {asstNames.map(name => (
              <button key={name} className={selectedAssts.has(name) ? 'pill-active' : 'pill-inactive'} onClick={() => toggleAsst(name)}>
                {name}
              </button>
            ))}
          </>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {saving && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Saving…</span>}
          {!saving && isFetching && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Syncing…</span>}
          <Button variant="ghost" size="sm" disabled={exporting} onClick={handleExport}>
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
          <Button variant={editMode ? 'primary' : 'default'} size="sm" onClick={() => { setEditMode(m => !m); setEditingCell(null); setShowAdd(false); setShowEvents(false); }}>
            {editMode ? '✓ Done editing' : 'Edit'}
          </Button>
          {editMode && (
            <Button variant="ai-soft" size="sm" onClick={() => setShowAdd(s => !s)}>+ Add SKU</Button>
          )}
          {editMode && (
            <Button variant={showEvents ? 'primary' : 'default'} size="sm" onClick={() => setShowEvents(s => !s)}>Events</Button>
          )}
        </div>
      </div>

      {saveErr && (
        <div style={{
          background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', color: 'var(--danger)',
          borderRadius: 'var(--radius)', padding: '8px 12px', fontSize: 12,
        }}>
          Could not save your last change: {String(saveErr.message || saveErr).slice(0, 200)}
        </div>
      )}

      {editMode && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          Editing mode — click any cell to set its value (Enter to save, Esc to cancel; empty clears the cell). Changes are saved instantly and recorded against your name.
        </div>
      )}

      {showAdd && <AddSkuPanel grid={grid} onAdd={handleAdd} onClose={() => setShowAdd(false)} busy={addAssortment.isPending} />}

      {showEvents && <EventsPanel events={grid.eventList} onSave={onSaveEvent} onDelete={onDeleteEvent} busy={upsertEvent.isPending || deleteEvent.isPending} />}

      {/* ── Calendar ── */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', overflowY: 'visible' }}>
          <table style={{ minWidth: 2900, fontSize: 11, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 130 }} />
              {WEEKS.map(w => <col key={w} style={{ width: 54 }} />)}
            </colgroup>

            <thead>
              <WeekHeaders keyDates={grid.keyDates} />
            </thead>

            <tbody>
              {/* ── Key Dates & Events section ── */}
              <tr>
                <td colSpan={53} style={{
                  background: 'var(--border)', padding: '5px 10px',
                  fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  borderBottom: '1px solid var(--border)',
                }}>
                  Key Dates &amp; Events
                </td>
              </tr>

              <EventRow label="Majans" data={grid.majansKeyDates} type="majans" />

              {showWW && <SpanEventRow label="WW Low Price"  spans={grid.wwCampaigns}      type="ww" />}
              {showWW && <SpanEventRow label="WW Seasonal"   spans={grid.wwSeasonalSpans}  type="ww" />}
              {showWW && <EventRow     label="WW Key Dates"  data={grid.wwKeyDates}        type="ww" />}

              {showColes && <SpanEventRow label="Coles Low Price" spans={grid.colesCampaigns} type="coles" />}
              {showColes && <EventRow     label="Coles Key Dates" data={grid.colesKeyDates}   type="coles" />}

              {STATES.map(state => (
                <SpanEventRow key={state} label={`School ${state}`} spans={grid.schoolTermsSpans[state] || []} type="school" />
              ))}

              {/* Extra sales lanes — any sales event with an unrecognised scope (never silently dropped) */}
              {grid.extraSalesLanes.map(lane => (
                <SpanEventRow key={`xs-${lane.scope}`} label={lane.label} spans={lane.spans} type="sales-extra" />
              ))}

              {/* ── Marketing events ── */}
              {grid.marketingLanes.length > 0 && (
                <tr>
                  <td colSpan={53} style={{
                    background: 'var(--border)', padding: '6px 10px',
                    fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    borderTop: '2px solid var(--border)', borderBottom: '1px solid var(--border)',
                  }}>
                    Marketing Activity — FY{FY}
                  </td>
                </tr>
              )}
              {grid.marketingLanes.map(lane => (
                <SpanEventRow key={`mkt-${lane.scope}`} label={lane.label} spans={lane.spans} type="marketing" />
              ))}

              {/* ── Promo assortments ── */}
              <tr>
                <td colSpan={53} style={{
                  background: 'var(--border)', padding: '6px 10px',
                  fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  borderTop: '2px solid var(--border)', borderBottom: '1px solid var(--border)',
                }}>
                  Promotional Activity — FY{FY}
                </td>
              </tr>

              {visiblePromos.map((p) => (
                <AssortmentSection
                  key={`${p.retailer}-${p.assortmentId}`}
                  promo={p}
                  retailer={p.retailer}
                  editMode={editMode}
                  editingCell={editingCell}
                  setEditingCell={setEditingCell}
                  onCommitCell={commitCell}
                  onRemove={handleRemove}
                />
              ))}

              {visiblePromos.length === 0 && (
                <tr>
                  <td colSpan={53} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                    No assortments match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Legend ── */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '0 4px' }}>
        {[
          { color: 'var(--success)', label: '50% OFF' },
          { color: 'var(--info)', label: 'Multi-buy' },
          { color: 'var(--danger)', label: 'Low Price' },
          { color: 'var(--ai)', label: 'Media/CAT' },
          { color: 'var(--warn)', label: 'Activation' },
          { color: 'var(--info)', label: 'Scan Deal' },
          { color: 'var(--text-muted)', label: 'Store Tie Up' },
        ].map(({ color, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
